/**
 * App-wide configuration constants and accessors.
 *
 * This module holds:
 *   - Tunable constants (timing, TTLs) used across the app.
 *   - Environment resolution helpers (`getCurrentEnvironment`, dev-menu overrides).
 *   - Static `config` object (terms / privacy URLs, color tokens, etc.).
 *
 * Future home for things like cookie-consent expiry windows, feature timing
 * thresholds, and similar configuration values.
 */
import { Colors } from './colors';
import {
  type Environment,
  type EnvironmentConfig,
  getEnvironmentConfigByName,
  isValidEnvironment,
} from './environments';
import { storage } from './storage';

/**
 * Time-to-live windows, in milliseconds. Add new TTLs here as siblings.
 */
export const TTL = {
  // 90 days. Matches the backend's rejection window for a Google Ads click
  // identifier (gclid/gbraid/wbraid) — see squire-api SQR-14872. Also governs
  // the existing analytics attribution (booking_completed, GA4
  // book_appointment); widening it only lets more legitimate late conversions
  // through, it never misattributes.
  googleAdsAttribution: 90 * 24 * 60 * 60 * 1000,
  // 30 days. Google's own attribution window for a Reserve with Google token —
  // deliberately NOT the 90-day googleAdsAttribution window above, which is
  // matched to squire-api's gclid rejection rule. SQR-16105.
  rwgAttribution: 30 * 24 * 60 * 60 * 1000,
} as const;

const STORAGE_KEY_SELECTED_ENV = '@dev/selected_environment';

/**
 * Get the current environment name
 * Priority:
 * 1. Runtime override from MMKV storage (for dev menu switching)
 * 2. Build-time EXPO_PUBLIC_RUNTIME_ENV
 * 3. Default to 'qa'
 */
export function getCurrentEnvironment(): Environment {
  const runtimeEnv = storage.getString(STORAGE_KEY_SELECTED_ENV);

  if (runtimeEnv && isValidEnvironment(runtimeEnv)) {
    return runtimeEnv;
  }

  const buildTimeEnv = process.env.EXPO_PUBLIC_RUNTIME_ENV;

  if (buildTimeEnv && isValidEnvironment(buildTimeEnv)) {
    return buildTimeEnv;
  }

  return 'qa';
}

/**
 * Set the runtime environment (used by dev menu)
 * This persists across app restarts
 */
export function setCurrentEnvironment(env: Environment): void {
  storage.set(STORAGE_KEY_SELECTED_ENV, env);
}

/**
 * Clear the runtime environment override
 * App will fall back to build-time env or default
 */
export function clearEnvironmentOverride(): void {
  storage.remove(STORAGE_KEY_SELECTED_ENV);
}

/**
 * Get the full environment configuration for the current environment
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const env = getCurrentEnvironment();
  return getEnvironmentConfigByName(env);
}

export const config = {
  termsUrl: 'https://getsquire.com/legal/terms',
  privacyUrl: 'https://getsquire.com/privacy-policy',
  colors: Colors,
  // Deprecated: Use getEnvironmentConfig() instead
  launchDarkly: {
    get mobileKey() {
      return getEnvironmentConfig().launchDarklyKey;
    },
    get clientSideId() {
      return getEnvironmentConfig().launchDarklyWebId;
    },
    get environment() {
      return getCurrentEnvironment();
    },
  },
};
